"use client";

import * as React from "react";
import { cn } from "@modernatc/ui/cn";

interface XpToastItem {
  id: string;
  amount: number;
  /** Optional new running total to surface alongside the delta. */
  total?: number;
  /** Optional new level — only rendered when a level-up happened. */
  leveledUpTo?: number;
}

/**
 * Lightweight `+N XP` toaster for per-action gratification.
 *
 * Distinct from `AchievementToaster`: achievement toasts are rare and
 * carry rich detail. XP toasts fire on *every* graded card / exam
 * question / phraseology attempt and need to feel snappy — small,
 * brief, no friction.
 *
 * Auto-dismisses after 1.6 s. Stacks vertically so back-to-back
 * grades don't smear; cap at 3 visible to avoid spam during rapid
 * answer streaks.
 */
export function XpToaster() {
  const [items, setItems] = React.useState<XpToastItem[]>([]);

  React.useEffect(() => {
    function onXp(ev: Event) {
      const detail = (ev as CustomEvent<Omit<XpToastItem, "id">>).detail;
      if (!detail || typeof detail.amount !== "number" || detail.amount <= 0) {
        return;
      }
      setItems((prev) => {
        const next = [
          ...prev,
          {
            ...detail,
            id: `xp:${Date.now()}:${Math.random().toString(36).slice(2, 6)}`,
          },
        ];
        return next.slice(-3);
      });
    }
    window.addEventListener("gamification:xp", onXp);
    return () => window.removeEventListener("gamification:xp", onXp);
  }, []);

  React.useEffect(() => {
    if (items.length === 0) return;
    const timers = items.map((it) =>
      setTimeout(() => {
        setItems((prev) => prev.filter((p) => p.id !== it.id));
      }, 1600),
    );
    return () => {
      for (const t of timers) clearTimeout(t);
    };
  }, [items]);

  if (items.length === 0) return null;
  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 flex-col items-center gap-1.5"
    >
      {items.map((it) => (
        <div
          key={it.id}
          className={cn(
            "rounded-full border border-[var(--color-amber)]/60 bg-[var(--color-panel)]/95 px-3 py-1 text-callsign text-xs shadow-[0_4px_18px_rgba(21,33,46,0.12)] backdrop-blur-sm",
            "animate-[xp-rise_1.6s_ease-out_forwards] text-[var(--color-amber-bright)]",
          )}
        >
          <span className="font-semibold">+{it.amount} XP</span>
          {it.leveledUpTo != null ? (
            <span className="ml-2 text-[var(--color-clear)]">
              Level {it.leveledUpTo}!
            </span>
          ) : null}
        </div>
      ))}
      <style>
        {`@keyframes xp-rise {
            0%   { opacity: 0; transform: translateY(8px) scale(0.96); }
            12%  { opacity: 1; transform: translateY(0)   scale(1);     }
            82%  { opacity: 1; transform: translateY(-6px); }
            100% { opacity: 0; transform: translateY(-14px); }
          }`}
      </style>
    </div>
  );
}

export interface EmitXpInput {
  amount: number;
  total?: number;
  /** New level if the action triggered a level-up. */
  leveledUpTo?: number;
}

/**
 * Fire the `+N XP` toast. Callers pass the delta the user just earned.
 * Safe to call with 0 — the toaster filters non-positive deltas so
 * "Again"-rated reviews don't show a `+0 XP` toast.
 */
export function emitXpGained(input: EmitXpInput): void {
  if (typeof window === "undefined") return;
  if (!Number.isFinite(input.amount) || input.amount <= 0) return;
  window.dispatchEvent(
    new CustomEvent("gamification:xp", { detail: input }),
  );
}
